import moment from 'moment'
import { fetchCompany } from '@/api/parameters'
import { moneda } from '@/utils/filtros'
import numeral from 'numeral'

export async function generateInventoryDashboardPdf(payload) {
  const pdfMake = require("pdfmake/build/pdfmake.js")
  const pdfFonts = require("pdfmake/build/vfs_fonts.js")
  pdfMake.vfs = pdfFonts.pdfMake.vfs

  // Datos de la empresa
  let company = payload.company || {}
  try {
    if (!company.name) {
      company = await fetchCompany()
    }
  } catch (e) {
    company = {}
  }

  const countryCode = company.country_code || 'cl'
  const companyName = company.fantasy || company.name || 'Runflow ERP'
  const companyRut = company.rut || ''
  const timezone = payload.serverTimezone || company.current_time_zone || 'America/Santiago'

  const formatMoney = (val) => moneda(val, countryCode)
  const formatNumber = (val, dec = 0) => {
    const num = Number(val || 0)
    return numeral(num).format(dec === 0 ? '0,0' : (dec === 2 ? '0,0.00' : '0,0.0'))
  }

  const asOfFormatted = payload.asOf ? moment(payload.asOf).format('DD/MM/YYYY') : moment().format('DD/MM/YYYY')
  const days = payload.days || 30
  const storeLabel = payload.storeLabel || 'Todas las bodegas'
  const familyLabel = payload.familyLabel || 'Todas las familias'
  const periodLabel = `Período: ${days} días · ${storeLabel} · ${familyLabel} · al ${asOfFormatted}`

  const now = moment()
  const emissionDate = now.format('DD/MM/YYYY HH:mm')

  const kpis = payload.kpis || {}
  const movement = payload.movement || {}
  const stores = payload.stores || []
  const families = payload.families || []
  const critical = payload.critical || []
  const topValue = payload.topValue || []
  const purchaseOrders = payload.purchaseOrders || []
  const zero = payload.zero || []
  const insight = payload.insight || null

  const totalInventoryValue = Number(kpis.value || 0)
  const pct = (part, tot) => ((Number(part || 0) / Math.max(1, tot)) * 100).toFixed(1)

  // Document Definition
  const dd = {
    pageSize: 'LETTER',
    pageOrientation: 'landscape',
    pageMargins: [28, 22, 28, 22],
    footer: function(currentPage, pageCount) {
      return {
        margin: [28, 0, 28, 0],
        columns: [
          { text: 'RUNFLOW ERP · MÓDULO DE INVENTARIOS Y GESTIÓN DE EXISTENCIAS', fontSize: 7, color: '#94A3B8', bold: true },
          { text: `Página ${currentPage} de ${pageCount}`, alignment: 'right', fontSize: 7, color: '#94A3B8', bold: true }
        ]
      }
    },
    content: [
      // ==========================================
      // PÁGINA 1: RESUMEN EJECUTIVO (ONE-PAGER)
      // ==========================================

      // 1. Header Banner - Midnight Slate (#0F172A)
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                fillColor: '#0F172A',
                border: [false, false, false, false],
                margin: [16, 12, 16, 12],
                columns: [
                  {
                    width: '*',
                    stack: [
                      {
                        text: 'RUNFLOW · INFORME EJECUTIVO DE INVENTARIOS Y EXISTENCIAS',
                        fontSize: 8,
                        bold: true,
                        color: '#00A650',
                        characterSpacing: 1.5,
                        margin: [0, 0, 0, 3]
                      },
                      {
                        text: `${companyName}`,
                        fontSize: 16,
                        bold: true,
                        color: '#FFFFFF',
                        margin: [0, 0, 0, 3]
                      },
                      {
                        text: `${companyRut ? 'RUT: ' + companyRut + '  ·  ' : ''}${periodLabel}`,
                        fontSize: 8.5,
                        color: '#CBD5E1'
                      }
                    ]
                  },
                  {
                    width: 220,
                    alignment: 'right',
                    stack: [
                      {
                        text: 'VALORIZACIÓN & AUDITORÍA DE STOCK',
                        fontSize: 7.5,
                        bold: true,
                        color: '#38BDF8',
                        margin: [0, 0, 0, 6]
                      },
                      {
                        text: `Emisión: ${emissionDate} hrs`,
                        fontSize: 8,
                        color: '#94A3B8',
                        margin: [0, 0, 0, 2]
                      },
                      {
                        text: `Zona Horaria: ${timezone}`,
                        fontSize: 7.5,
                        color: '#64748B'
                      }
                    ]
                  }
                ]
              }
            ]
          ]
        },
        margin: [0, 0, 0, 10],
        layout: {
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0
        }
      },

      // 2. Fila Héroe de 4 KPIs Maestros
      {
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            [
              // Card 1: Inventario Valorizado (North Star)
              {
                fillColor: '#0F172A',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: 'INVENTARIO VALORIZADO', fontSize: 7, bold: true, color: '#00A650', characterSpacing: 1 },
                  { text: formatMoney(kpis.value || 0), fontSize: 15, bold: true, color: '#FFFFFF', margin: [0, 3, 0, 3] },
                  {
                    text: `${formatNumber(kpis.with_stock, 0)} SKU con existencia activa`,
                    fontSize: 7.5,
                    color: '#94A3B8'
                  }
                ]
              },
              // Card 2: Artículos Activos
              {
                fillColor: '#F8FAFC',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: 'ARTÍCULOS EN CATÁLOGO', fontSize: 7, bold: true, color: '#64748B', characterSpacing: 1 },
                  { text: `${formatNumber(kpis.articles, 0)} SKU`, fontSize: 15, bold: true, color: '#0F172A', margin: [0, 3, 0, 3] },
                  { text: `${formatNumber(kpis.qty, 0)} unidades físicas en total`, fontSize: 7.5, color: '#64748B' }
                ]
              },
              // Card 3: Stock Crítico
              {
                fillColor: '#F8FAFC',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: 'STOCK CRÍTICO', fontSize: 7, bold: true, color: '#64748B', characterSpacing: 1 },
                  {
                    text: `${kpis.critical || 0} SKU`,
                    fontSize: 15,
                    bold: true,
                    color: (kpis.critical || 0) > 0 ? '#BE123C' : '#047857',
                    margin: [0, 3, 0, 3]
                  },
                  { text: `${kpis.zero_stock || 0} artículos activos en cero`, fontSize: 7.5, color: '#64748B' }
                ]
              },
              // Card 4: OC en Tránsito
              {
                fillColor: '#F8FAFC',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: 'OC POR RECEPCIONAR', fontSize: 7, bold: true, color: '#64748B', characterSpacing: 1 },
                  { text: formatMoney(kpis.pending_po_value || 0), fontSize: 15, bold: true, color: '#0284C7', margin: [0, 3, 0, 3] },
                  { text: `${kpis.pending_pos || 0} órdenes de compra abiertas`, fontSize: 7.5, color: '#64748B' }
                ]
              }
            ]
          ]
        },
        margin: [0, 0, 0, 10],
        layout: {
          defaultBorder: false,
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 3,
          paddingRight: () => 3,
          paddingTop: () => 0,
          paddingBottom: () => 0
        }
      },

      // Diagnóstico Ejecutivo de Inventario (Insight)
      ...(insight ? [
        {
          table: {
            widths: ['*'],
            body: [
              [
                {
                  fillColor: insight.severity === 'error' ? '#1E1B2E' : (insight.severity === 'warning' ? '#241D13' : '#0F172A'),
                  border: [false, false, false, false],
                  margin: [12, 6, 12, 6],
                  columns: [
                    {
                      width: '*',
                      stack: [
                        {
                          text: `DIAGNÓSTICO: ${insight.title ? insight.title.toUpperCase() : 'LECTURA DEL INVENTARIO'}`,
                          fontSize: 7.5,
                          bold: true,
                          color: insight.severity === 'error' ? '#F87171' : (insight.severity === 'warning' ? '#FBBF24' : '#38BDF8'),
                          margin: [0, 0, 0, 2]
                        },
                        {
                          text: insight.body || '',
                          fontSize: 7,
                          color: '#E2E8F0'
                        }
                      ]
                    },
                    ...(insight.highlight ? [
                      {
                        width: 170,
                        alignment: 'right',
                        text: `● ${insight.highlight}`,
                        fontSize: 7.5,
                        bold: true,
                        color: insight.severity === 'error' ? '#FCA5A5' : (insight.severity === 'warning' ? '#FDE68A' : '#7DD3FC')
                      }
                    ] : [])
                  ]
                }
              ]
            ]
          },
          margin: [0, 0, 0, 8],
          layout: {
            defaultBorder: false,
            hLineWidth: () => 0,
            vLineWidth: () => 0
          }
        }
      ] : []),

      // 3. Bloque Intermedio: Balance de Flujo (Entradas vs Salidas) + Distribución de Bodegas
      {
        columns: [
          // Left: Balance de Flujo de Bodega
          {
            width: 360,
            stack: [
              {
                text: `FLUJO DE MOVIMIENTOS EN BODEGA (${days} DÍAS)`,
                fontSize: 8,
                bold: true,
                color: '#475569',
                characterSpacing: 0.8,
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  widths: ['50%', '50%'],
                  body: [
                    [
                      // Box Entradas
                      {
                        fillColor: '#F0FDF4',
                        border: [false, false, false, false],
                        margin: [8, 8, 8, 8],
                        stack: [
                          { text: 'ENTRADAS ALMACENADAS', fontSize: 7, bold: true, color: '#047857' },
                          { text: formatMoney(movement.in_value || 0), fontSize: 12, bold: true, color: '#047857', margin: [0, 2, 0, 2] },
                          { text: `${formatNumber(movement.in_qty, 1)} un · ${movement.in_count || 0} movimientos`, fontSize: 7, color: '#065F46' }
                        ]
                      },
                      // Box Salidas
                      {
                        fillColor: '#FFF1F2',
                        border: [false, false, false, false],
                        margin: [8, 8, 8, 8],
                        stack: [
                          { text: 'SALIDAS / CONSUMOS', fontSize: 7, bold: true, color: '#BE123C' },
                          { text: formatMoney(movement.out_value || 0), fontSize: 12, bold: true, color: '#BE123C', margin: [0, 2, 0, 2] },
                          { text: `${formatNumber(movement.out_qty, 1)} un · ${movement.out_count || 0} movimientos`, fontSize: 7, color: '#9F1239' }
                        ]
                      }
                    ],
                    [
                      // Box Con Stock
                      {
                        fillColor: '#F8FAFC',
                        border: [false, false, false, false],
                        margin: [8, 6, 8, 6],
                        stack: [
                          { text: 'ARTÍCULOS CON STOCK', fontSize: 7, bold: true, color: '#0284C7' },
                          { text: `${formatNumber(kpis.with_stock, 0)} SKU`, fontSize: 11, bold: true, color: '#0F172A' },
                          { text: `de ${formatNumber(kpis.articles, 0)} artículos catalogados`, fontSize: 6.5, color: '#64748B' }
                        ]
                      },
                      // Box Sin Stock
                      {
                        fillColor: '#F8FAFC',
                        border: [false, false, false, false],
                        margin: [8, 6, 8, 6],
                        stack: [
                          { text: 'ARTÍCULOS EN CERO', fontSize: 7, bold: true, color: '#B45309' },
                          { text: `${formatNumber(kpis.zero_stock, 0)} SKU`, fontSize: 11, bold: true, color: '#0F172A' },
                          { text: 'artículos activos sin existencia física', fontSize: 6.5, color: '#64748B' }
                        ]
                      }
                    ]
                  ]
                },
                layout: {
                  defaultBorder: false,
                  hLineWidth: () => 0,
                  vLineWidth: () => 0,
                  paddingLeft: () => 2,
                  paddingRight: () => 2,
                  paddingTop: () => 2,
                  paddingBottom: () => 2
                }
              }
            ]
          },
          // Gap: 15 pt
          { width: 15, text: '' },
          // Right: Valor por Bodega
          {
            width: 360,
            stack: [
              {
                text: 'VALORIZACIÓN POR BODEGA',
                fontSize: 8,
                bold: true,
                color: '#475569',
                characterSpacing: 0.8,
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  headerRows: 1,
                  dontBreakRows: true,
                  widths: ['*', 50, 75, 45],
                  body: [
                    [
                      { text: 'BODEGA', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                      { text: 'SKUs', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'center' },
                      { text: 'MONTO VALORIZADO', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                      { text: '% DEL TOTAL', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' }
                    ],
                    ...(stores.slice(0, 5).length ? stores.slice(0, 5).map((s) => [
                      { text: s.name || 'Sin nombre', fontSize: 7.5, bold: true, color: '#0F172A' },
                      { text: String(s.skus || 0), fontSize: 7.5, alignment: 'center', color: '#475569' },
                      { text: formatMoney(s.value || 0), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A' },
                      { text: `${pct(s.value, totalInventoryValue)}%`, fontSize: 7.5, alignment: 'right', color: '#64748B' }
                    ]) : [
                      [{ text: 'Sin bodegas valorizadas', colSpan: 4, alignment: 'center', fontSize: 7.5, color: '#94A3B8' }, {}, {}, {}]
                    ])
                  ]
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0,
                  hLineColor: () => '#E2E8F0',
                  paddingLeft: () => 4,
                  paddingRight: () => 4,
                  paddingTop: () => 4,
                  paddingBottom: () => 4
                }
              }
            ]
          }
        ],
        margin: [0, 0, 0, 10]
      },

      // 4. Bloque Inferior: Top Familias y Mayor Capital Inmovilizado
      {
        columns: [
          // Table Left: Top Familias
          {
            width: 360,
            stack: [
              {
                text: 'VALORIZACIÓN POR FAMILIA DE PRODUCTO',
                fontSize: 8,
                bold: true,
                color: '#475569',
                characterSpacing: 0.8,
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  headerRows: 1,
                  dontBreakRows: true,
                  widths: ['*', 50, 75, 45],
                  body: [
                    [
                      { text: 'FAMILIA', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                      { text: 'SKUs', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'center' },
                      { text: 'MONTO VALORIZADO', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                      { text: '% DEL TOTAL', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' }
                    ],
                    ...(families.slice(0, 5).length ? families.slice(0, 5).map((f) => [
                      { text: f.name || 'Sin familia', fontSize: 7.5, bold: true, color: '#0F172A' },
                      { text: String(f.skus || 0), fontSize: 7.5, alignment: 'center', color: '#475569' },
                      { text: formatMoney(f.value || 0), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A' },
                      { text: `${pct(f.value, totalInventoryValue)}%`, fontSize: 7.5, alignment: 'right', color: '#64748B' }
                    ]) : [
                      [{ text: 'Sin familias registradas', colSpan: 4, alignment: 'center', fontSize: 7.5, color: '#94A3B8' }, {}, {}, {}]
                    ])
                  ]
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0,
                  hLineColor: () => '#E2E8F0',
                  paddingLeft: () => 4,
                  paddingRight: () => 4,
                  paddingTop: () => 4,
                  paddingBottom: () => 4
                }
              }
            ]
          },
          // Gap: 15 pt
          { width: 15, text: '' },
          // Table Right: Top 5 Mayor Capital Inmovilizado
          {
            width: 360,
            stack: [
              {
                text: 'MAYOR CAPITAL INMOVILIZADO (TOP 5 SKU)',
                fontSize: 8,
                bold: true,
                color: '#475569',
                characterSpacing: 0.8,
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  headerRows: 1,
                  dontBreakRows: true,
                  widths: [60, '*', 55, 75],
                  body: [
                    [
                      { text: 'SKU', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                      { text: 'ARTÍCULO', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                      { text: 'STOCK', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                      { text: 'VALOR TOTAL', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' }
                    ],
                    ...(topValue.slice(0, 5).length ? topValue.slice(0, 5).map((v) => [
                      { text: v.sku || '—', fontSize: 7.5, bold: true, color: '#0284C7' },
                      { text: v.name || 'Sin nombre', fontSize: 7.5, color: '#0F172A' },
                      { text: `${formatNumber(v.qty, 1)} ${v.unit || ''}`, fontSize: 7.5, alignment: 'right', color: '#475569' },
                      { text: formatMoney(v.value || 0), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A' }
                    ]) : [
                      [{ text: 'Sin valorización', colSpan: 4, alignment: 'center', fontSize: 7.5, color: '#94A3B8' }, {}, {}, {}]
                    ])
                  ]
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0,
                  hLineColor: () => '#E2E8F0',
                  paddingLeft: () => 4,
                  paddingRight: () => 4,
                  paddingTop: () => 4,
                  paddingBottom: () => 4
                }
              }
            ]
          }
        ]
      },

      // ========================================================
      // PÁGINA 2: ANEXO DE STOCK CRÍTICO Y COMPRAS PENDIENTES
      // ========================================================
      ...(critical.length || purchaseOrders.length ? [
        { text: '', pageBreak: 'before' },
        // Cabecera Secundaria de Anexo
        {
          table: {
            widths: ['*'],
            body: [
              [
                {
                  fillColor: '#F8FAFC',
                  border: [false, false, false, false],
                  margin: [12, 8, 12, 8],
                  columns: [
                    {
                      text: 'ANEXO DE AUDITORÍA: STOCK CRÍTICO Y COMPRAS EN TRÁNSITO',
                      fontSize: 10,
                      bold: true,
                      color: '#0F172A'
                    },
                    {
                      text: `${companyName} · ${periodLabel}`,
                      fontSize: 8,
                      color: '#64748B',
                      alignment: 'right'
                    }
                  ]
                }
              ]
            ]
          },
          margin: [0, 0, 0, 10],
          layout: {
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0
          }
        },

        // Tabla 1: Stock Crítico
        {
          text: 'ARTÍCULOS CON STOCK CRÍTICO (BAJO EL MÍNIMO OPERATIVO)',
          fontSize: 8.5,
          bold: true,
          color: '#BE123C',
          characterSpacing: 0.5,
          margin: [0, 0, 0, 4]
        },
        {
          table: {
            headerRows: 1,
            dontBreakRows: true,
            widths: [70, '*', 100, 60, 60, 60],
            body: [
              [
                { text: 'SKU', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                { text: 'ARTÍCULO', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                { text: 'FAMILIA', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                { text: 'EXISTENCIA', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                { text: 'MÍNIMO', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                { text: 'FALTANTE', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' }
              ],
              ...(critical.length ? critical.slice(0, 14).map((c) => [
                { text: c.sku || `ID ${c.id}`, fontSize: 7.5, bold: true, color: '#BE123C' },
                { text: c.name || 'Sin nombre', fontSize: 7.5, bold: true, color: '#0F172A' },
                { text: c.family || 'Sin familia', fontSize: 7.5, color: '#64748B' },
                { text: `${formatNumber(c.qty, 1)} ${c.unit || ''}`, fontSize: 7.5, alignment: 'right', bold: true, color: '#BE123C' },
                { text: `${formatNumber(c.critic, 1)} ${c.unit || ''}`, fontSize: 7.5, alignment: 'right', color: '#475569' },
                { text: `-${formatNumber(c.gap, 1)}`, fontSize: 7.5, bold: true, alignment: 'right', color: '#DC2626' }
              ]) : [
                [{ text: 'Sin artículos bajo crítico', colSpan: 6, alignment: 'center', fontSize: 7.5, color: '#94A3B8' }, {}, {}, {}, {}, {}]
              ])
            ]
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0,
            hLineColor: () => '#E2E8F0',
            paddingLeft: () => 6,
            paddingRight: () => 6,
            paddingTop: () => 4,
            paddingBottom: () => 4
          },
          margin: [0, 0, 0, 12]
        },

        // Tabla 2: Órdenes de Compra Abiertas
        {
          text: 'ÓRDENES DE COMPRA ABIERTAS (PENDIENTES DE RECEPCIÓN)',
          fontSize: 8.5,
          bold: true,
          color: '#B45309',
          characterSpacing: 0.5,
          margin: [0, 0, 0, 4]
        },
        {
          table: {
            headerRows: 1,
            dontBreakRows: true,
            widths: [50, 70, 120, '*', 75, 65],
            body: [
              [
                { text: '# OC', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'center' },
                { text: 'ESTADO', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                { text: 'PROVEEDOR', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                { text: 'DETALLE / TÍTULO', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                { text: 'MONTO COMPRA', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                { text: 'UNID. PEND.', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' }
              ],
              ...(purchaseOrders.length ? purchaseOrders.slice(0, 10).map((po) => [
                { text: `#${po.id}`, fontSize: 7.5, bold: true, color: '#0F172A', alignment: 'center' },
                { text: po.estatus_label || 'Pendiente', fontSize: 7.5, color: '#B45309', bold: true },
                { text: po.provider || 'Sin proveedor', fontSize: 7.5, bold: true, color: '#0F172A' },
                { text: po.title || 'Sin detalle', fontSize: 7.5, color: '#475569' },
                { text: formatMoney(po.value || 0), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A' },
                { text: `${formatNumber(po.qty, 1)} un`, fontSize: 7.5, alignment: 'right', color: '#64748B' }
              ]) : [
                [{ text: 'No hay órdenes de compra pendientes', colSpan: 6, alignment: 'center', fontSize: 7.5, color: '#94A3B8' }, {}, {}, {}, {}, {}]
              ])
            ]
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0,
            hLineColor: () => '#E2E8F0',
            paddingLeft: () => 6,
            paddingRight: () => 6,
            paddingTop: () => 4,
            paddingBottom: () => 4
          }
        }
      ] : [])
    ]
  }

  const fileName = `Reporte_Ejecutivo_Inventarios_${days}dias_${moment().format('YYYYMMDD')}.pdf`
  pdfMake.createPdf(dd).download(fileName)
}
